import { FiLayers } from 'react-icons/fi';

export const wordFamiliesData = {
  courseId: 'word-families',
  courseType: 'vocabulary',
  title: 'Word Families',
  icon: FiLayers,
  overview: {
    impact: 'Recognise and use noun, verb, adjective, and adverb forms from a single root — essential for accurate IELTS Writing and Reading',
    focus: 'Word formation patterns, common suffixes by word class, and choosing the correct form in context',
    uses: 'Writing Task 1 & 2 accuracy, Reading gap-fills, Speaking lexical resource',
    duration: '20-25 minutes'
  },

  sections: [
    {
      title: 'Understanding Word Families',
      content: `<p>A <strong>word family</strong> is a group of words that share the same root but differ in form (part of speech). Knowing word families lets you express the same idea as a noun, verb, adjective, or adverb — a key skill tested in IELTS Reading and Writing.</p>
<table>
  <thead><tr><th>Root</th><th>Noun</th><th>Verb</th><th>Adjective</th><th>Adverb</th></tr></thead>
  <tbody>
    <tr><td>employ</td><td>employment, employee, employer</td><td>employ</td><td>employable</td><td>—</td></tr>
    <tr><td>economy</td><td>economy, economist</td><td>economise</td><td>economic, economical</td><td>economically</td></tr>
    <tr><td>create</td><td>creation, creativity, creator</td><td>create</td><td>creative</td><td>creatively</td></tr>
    <tr><td>success</td><td>success</td><td>succeed</td><td>successful</td><td>successfully</td></tr>
    <tr><td>science</td><td>science, scientist</td><td>—</td><td>scientific</td><td>scientifically</td></tr>
  </tbody>
</table>`,
      points: [
        'A single root can generate 3–6 related words across parts of speech',
        'Some roots have multiple noun forms with different meanings (e.g., "economy" = system, "economist" = person)',
        'Not every root produces all four forms — gaps are normal (e.g., "science" has no standard verb)'
      ]
    },
    {
      title: 'Noun Suffixes: -tion, -ment, -ness, -ity',
      content: `<p>Nouns are often formed by adding a suffix to a verb or adjective root. Recognising these patterns helps you choose the right form in sentence gaps.</p>
<table>
  <thead><tr><th>Suffix</th><th>Typical base</th><th>Examples</th></tr></thead>
  <tbody>
    <tr><td>-tion / -sion</td><td>Verb → Noun</td><td>educate → education, decide → decision</td></tr>
    <tr><td>-ment</td><td>Verb → Noun</td><td>develop → development, achieve → achievement</td></tr>
    <tr><td>-ness</td><td>Adjective → Noun</td><td>happy → happiness, aware → awareness</td></tr>
    <tr><td>-ity / -ty</td><td>Adjective → Noun</td><td>diverse → diversity, creative → creativity</td></tr>
    <tr><td>-ence / -ance</td><td>Verb/Adj → Noun</td><td>depend → dependence, important → importance</td></tr>
  </tbody>
</table>`,
      points: [
        'Spelling changes sometimes occur: "happy" drops the y → "happiness"',
        'Some words accept more than one noun suffix with different meanings: "move → movement" (action) vs "move → mover" (person)',
        'In IELTS Reading, recognising noun suffixes helps you locate answers quickly'
      ]
    },
    {
      title: 'Verb, Adjective & Adverb Suffixes',
      content: `<p>Beyond nouns, each word class has characteristic endings that signal its grammatical role.</p>
<table>
  <thead><tr><th>Word class</th><th>Common suffixes</th><th>Examples</th></tr></thead>
  <tbody>
    <tr><td>Verb</td><td>-ise / -ize, -en, -ify, -ate</td><td>modern → modernise, strength → strengthen, simple → simplify, active → activate</td></tr>
    <tr><td>Adjective</td><td>-al, -ive, -ous, -ful, -able / -ible</td><td>nature → natural, create → creative, danger → dangerous, use → useful, rely → reliable</td></tr>
    <tr><td>Adverb</td><td>-ly (most common)</td><td>quick → quickly, significant → significantly</td></tr>
  </tbody>
</table>`,
      points: [
        'Adverbs almost always end in -ly, but watch exceptions: fast, hard, well',
        'The suffix -able/-ible means "can be done": affordable, accessible',
        'Adjectives ending in -ive often come from Latin-origin verbs: attract → attractive'
      ]
    },
    {
      title: 'Choosing the Correct Form in Context',
      content: `<p>In IELTS Writing and Reading, using the wrong word form is a common error that costs marks. Use these grammar cues to pick the right form:</p>
<ul>
  <li><strong>After articles (a, an, the) or adjectives →</strong> use a <em>noun</em>: "The <u>development</u> of technology…"</li>
  <li><strong>Before a noun →</strong> use an <em>adjective</em>: "…<u>economic</u> growth"</li>
  <li><strong>After a subject →</strong> use a <em>verb</em>: "Governments should <u>regulate</u>…"</li>
  <li><strong>Modifying a verb or adjective →</strong> use an <em>adverb</em>: "It increased <u>significantly</u>."</li>
</ul>`,
      points: [
        'Read the sentence structure before filling in a word — identify what part of speech is needed',
        'Common IELTS error: using an adjective where a noun is needed ("The *creative* of art" ✗ → "The *creation* of art" ✓)',
        'Practice transforming a single root into all four forms and using each in a sentence'
      ]
    }
  ],

  exercises: [
    {
      title: 'Identify the Word Family',
      type: 'select',
      instructions: 'For each root word, choose the option that correctly lists its noun, verb, adjective, and adverb forms.',
      questions: [
        {
          text: 'Which set correctly shows the word family for "develop"?',
          options: [
            'development, develop, developmental, developmentally',
            'developer, developing, developable, developly',
            'developness, developise, developful, develope',
            'developing, develop, developed, developingly'
          ],
          answer: 'development, develop, developmental, developmentally',
          explanation: '"Development" is the noun, "develop" the verb, "developmental" the adjective, and "developmentally" the adverb.'
        },
        {
          text: 'Which set correctly shows the word family for "analyse"?',
          options: [
            'analysis, analyse, analytical, analytically',
            'analysement, analysize, analyseful, analysely',
            'analyser, analysing, analysive, analysely',
            'analysation, analyse, analysable, analysably'
          ],
          answer: 'analysis, analyse, analytical, analytically',
          explanation: '"Analysis" is the noun, "analyse" the verb, "analytical" the adjective, and "analytically" the adverb.'
        },
        {
          text: 'Which set correctly shows the word family for "signify"?',
          options: [
            'significance, signify, significant, significantly',
            'signification, signify, signiful, signifily',
            'signifier, signify, signifying, signifiedly',
            'significancy, signify, significal, significally'
          ],
          answer: 'significance, signify, significant, significantly',
          explanation: '"Significance" is the noun, "signify" the verb, "significant" the adjective, and "significantly" the adverb.'
        },
        {
          text: 'Which set correctly shows the word family for "vary"?',
          options: [
            'variation, vary, variable, variably',
            'variety, variise, varietal, varietally',
            'variance, vary, various, variously',
            'Both A and C are acceptable'
          ],
          answer: 'Both A and C are acceptable',
          explanation: '"Vary" has multiple valid noun and adjective forms: variation/variance/variety (nouns), variable/various (adjectives).'
        }
      ]
    },
    {
      title: 'Complete with the Correct Form',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct form of the word given in brackets.',
      questions: [
        {
          text: 'The government made a major _______ to education funding last year. (invest)',
          answer: 'investment',
          explanation: 'After the article "a" and adjective "major", a noun is needed. "Investment" is the noun form of "invest".'
        },
        {
          text: 'Researchers need to _______ the data before drawing conclusions. (analysis)',
          answer: 'analyse',
          explanation: 'After "to", a verb in base form is needed. "Analyse" is the verb form of "analysis".'
        },
        {
          text: 'There has been a _______ increase in online shopping. (significance)',
          answer: 'significant',
          explanation: 'Before the noun "increase", an adjective is needed. "Significant" is the adjective form of "significance".'
        },
        {
          text: 'The new policy was implemented _______ across all departments. (success)',
          answer: 'successfully',
          explanation: 'Modifying the verb "implemented", an adverb is needed. "Successfully" is the adverb form of "success".'
        },
        {
          text: 'Climate change poses a serious threat to _______ diversity. (biology)',
          answer: 'biological',
          explanation: 'Before the noun "diversity", an adjective is needed. "Biological" is the adjective form of "biology".'
        }
      ]
    },
    {
      title: 'Choose the Correct Word Class',
      type: 'select',
      instructions: 'Select the sentence that uses the correct word form.',
      questions: [
        {
          text: 'Which sentence is correct?',
          options: [
            'The economical situation has improved recently.',
            'The economic situation has improved recently.'
          ],
          answer: 'The economic situation has improved recently.',
          explanation: '"Economic" means "relating to the economy". "Economical" means "good value / not wasteful". The country\'s situation relates to the economy, so "economic" is correct.'
        },
        {
          text: 'Which sentence is correct?',
          options: [
            'She responded to the question very creative.',
            'She responded to the question very creatively.'
          ],
          answer: 'She responded to the question very creatively.',
          explanation: 'An adverb is needed to modify the verb "responded". "Creatively" is the adverb; "creative" is an adjective.'
        },
        {
          text: 'Which sentence is correct?',
          options: [
            'The maintain of old buildings is expensive.',
            'The maintenance of old buildings is expensive.'
          ],
          answer: 'The maintenance of old buildings is expensive.',
          explanation: 'After the article "the", a noun is required. "Maintenance" is the noun form; "maintain" is a verb.'
        },
        {
          text: 'Which sentence is correct?',
          options: [
            'It is importance to protect natural habitats.',
            'It is important to protect natural habitats.'
          ],
          answer: 'It is important to protect natural habitats.',
          explanation: 'After "It is", an adjective is needed. "Important" is the adjective; "importance" is the noun.'
        }
      ]
    },
    {
      title: 'Write Sentences Using Different Forms',
      type: 'writing',
      instructions: 'Write a sentence using the specified word form. Aim for sentences suitable for IELTS Writing Task 2.',
      questions: [
        {
          text: 'Write a sentence using the noun form of "pollute".',
          example: 'Air pollution in major cities has reached dangerous levels, prompting governments to introduce stricter regulations.',
          tip: 'The noun form is "pollution". Use it as the subject or object of your sentence in an academic context.'
        },
        {
          text: 'Write a sentence using the adverb form of "effective".',
          example: 'Online learning can effectively supplement traditional classroom instruction when properly designed.',
          tip: 'The adverb "effectively" should modify a verb. Place it before or after the verb it describes.'
        },
        {
          text: 'Write a sentence using all four forms of "educate" (noun, verb, adjective, adverb).',
          example: 'Education systems should educate students about environmental issues through educational programmes that are designed educationally to promote critical thinking.',
          tip: 'Forms: education (n), educate (v), educational (adj), educationally (adv). This exercise tests your awareness of all forms.'
        }
      ]
    }
  ],

  tips: [
    'Keep a vocabulary notebook organised by word family — write the noun, verb, adjective, and adverb in a row',
    'When you learn a new word, immediately look up its other forms in a dictionary',
    'In IELTS Reading, the answer may be a different form of a word you see in the question — look for the whole family',
    'Practice transforming sentences: "The economy grew" → "Economic growth occurred" → "It grew economically"'
  ],
  dailyPractice: [
    'Choose 3 new root words each day and write out their full word family',
    'Write one IELTS-style sentence for each form (noun, verb, adjective, adverb)',
    'Read an English article and underline words — then identify their word families',
    'Do 5 minutes of gap-fill exercises focusing on choosing the correct word form'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Word Families: Noun, verb, adjective, and adverb forms from a single root. Covers common suffixes by word class (-tion, -ment, -ness, -ity for nouns; -ise, -en, -ify for verbs; -al, -ive, -ous, -ful for adjectives; -ly for adverbs), and choosing the correct form in context. Difficulty 2 (pre-intermediate). Exercises include identification, gap-fill, error correction, and sentence writing.'
};

export function getDataForDifficulty(difficulty = 2) {
  if (difficulty <= 2) {
    return {
      ...wordFamiliesData,
      title: wordFamiliesData.title + ' (Foundations)',
      overview: { ...wordFamiliesData.overview, duration: '15-20 minutes' },
      sections: wordFamiliesData.sections.slice(0, Math.max(2, Math.ceil(wordFamiliesData.sections.length / 2))),
      exercises: wordFamiliesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...wordFamiliesData,
      title: wordFamiliesData.title + ' (Advanced)',
      overview: { ...wordFamiliesData.overview, duration: '30-35 minutes' },
      sections: wordFamiliesData.sections,
      exercises: wordFamiliesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(wordFamiliesData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return wordFamiliesData;
}
